'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

type Category = 'All' | 'Industry Insights' | 'Case Studies' | 'Hiring Trends' | 'Leadership' | 'Compliance';

interface BlogPost {
  id: string;
  category: Exclude<Category, 'All'>;
  title: string;
  excerpt: string;
  author: string;
  authorRole: string;
  date: string;
  readTime: string;
  tags: string[];
  featured?: boolean;
}

const posts: BlogPost[] = [
  {
    id: 'it-talent-market-2025',
    category: 'Industry Insights',
    title: 'The 2025 IT Talent Market: What Every Hiring Manager Needs to Know Before Q3',
    excerpt:
      'Demand for cloud architects, AI/ML engineers, and cybersecurity specialists is outpacing supply by a widening margin. We break down the data, the hottest skill sets, and the compensation shifts reshaping how companies compete for technical talent.',
    author: 'Marcus Webb',
    authorRole: 'VP of Talent Strategy',
    date: 'July 22, 2025',
    readTime: '8 min read',
    tags: ['Talent Market', 'IT Hiring', 'Compensation'],
    featured: true,
  },
  {
    id: 'fintech-cloud-migration',
    category: 'Case Studies',
    title: 'How a FinTech Firm Scaled Its Cloud Team by 45 Engineers in Under 60 Days',
    excerpt:
      'When a regulatory deadline threatened a $40M cloud migration, TranceformRIGHT deployed a curated sourcing model that cut time-to-hire from 11 weeks to 12 days. Here is exactly how we did it — and what other firms can replicate.',
    author: 'Priya Nair',
    authorRole: 'Director of Client Solutions',
    date: 'July 10, 2025',
    readTime: '6 min read',
    tags: ['FinTech', 'Cloud', 'RPO'],
    featured: true,
  },
  {
    id: 'contingent-workforce-compliance',
    category: 'Compliance',
    title: '1099 vs. W-2: The Misclassification Risk Most IT Leaders Are Ignoring',
    excerpt:
      'IRS enforcement of worker misclassification has intensified. We outline the five most common triggers for audits in technology staffing, the financial exposure companies face, and a practical compliance checklist your legal team will thank you for.',
    author: 'Dana Holloway',
    authorRole: 'Compliance & Risk Lead',
    date: 'June 28, 2025',
    readTime: '7 min read',
    tags: ['Compliance', '1099', 'Risk Management'],
  },
  {
    id: 'rpo-vs-traditional-staffing',
    category: 'Industry Insights',
    title: 'RPO vs. Traditional Staffing: A Straight-Talk Comparison for Technology Leaders',
    excerpt:
      'Recruitment Process Outsourcing is not right for every company — but for high-volume, fast-scaling technology organizations, the cost-per-hire and quality-of-hire advantages are hard to ignore. We lay out the real numbers.',
    author: 'James Okafor',
    authorRole: 'Senior Account Executive',
    date: 'June 15, 2025',
    readTime: '5 min read',
    tags: ['RPO', 'Staffing Models', 'Cost Analysis'],
  },
  {
    id: 'healthcare-it-hiring',
    category: 'Case Studies',
    title: 'Rebuilding a Healthcare IT Department Post-Merger: 118 Hires in 9 Months',
    excerpt:
      'After a major hospital network merger, two IT departments needed to become one — fast. TranceformRIGHT embedded a six-person RPO team and cut average time-to-hire from 87 days to 31. The full story, by the numbers.',
    author: 'Priya Nair',
    authorRole: 'Director of Client Solutions',
    date: 'June 3, 2025',
    readTime: '7 min read',
    tags: ['Healthcare', 'RPO', 'HIPAA'],
  },
  {
    id: 'remote-hiring-best-practices',
    category: 'Hiring Trends',
    title: 'Remote-First IT Hiring in 2025: What Works, What Fails, and What the Data Says',
    excerpt:
      'Three years of post-pandemic remote hiring data reveals a clear pattern: companies that standardize their virtual interview process see 40% higher offer acceptance rates. We share the framework our highest-performing clients use.',
    author: 'Leila Farooq',
    authorRole: 'Talent Acquisition Strategist',
    date: 'May 20, 2025',
    readTime: '6 min read',
    tags: ['Remote Hiring', 'Best Practices', 'Talent Acquisition'],
  },
  {
    id: 'cto-talent-retention',
    category: 'Leadership',
    title: 'Why Your Best Engineers Leave — and the Retention Levers CTOs Actually Control',
    excerpt:
      'Compensation is rarely the primary reason top engineers resign. Based on exit interview data from 600+ IT professionals, we identify the five retention levers that matter most — and how to act on them before your next resignation letter arrives.',
    author: 'Marcus Webb',
    authorRole: 'VP of Talent Strategy',
    date: 'May 8, 2025',
    readTime: '9 min read',
    tags: ['Retention', 'Leadership', 'Engineering Culture'],
  },
  {
    id: 'msp-contingent-workforce',
    category: 'Case Studies',
    title: 'From 12 Vendors to One Program: How an MSP Saved a Manufacturer $1.8M Annually',
    excerpt:
      'A global technology manufacturer was hemorrhaging budget across a fragmented contingent workforce. TranceformRIGHT consolidated 400+ workers under a single MSP program, standardized bill rates, and delivered measurable savings within the first quarter.',
    author: 'Dana Holloway',
    authorRole: 'Compliance & Risk Lead',
    date: 'April 24, 2025',
    readTime: '5 min read',
    tags: ['MSP', 'Cost Savings', 'Workforce Management'],
  },
  {
    id: 'diversity-tech-hiring',
    category: 'Hiring Trends',
    title: 'Building Diverse Technology Teams: What the Evidence Actually Shows',
    excerpt:
      'DEI hiring initiatives in tech often stall because they focus on top-of-funnel diversity without addressing the structural barriers in technical interviews and offer stages. Here is what the research says — and what actually moves the needle.',
    author: 'Leila Farooq',
    authorRole: 'Talent Acquisition Strategist',
    date: 'April 11, 2025',
    readTime: '7 min read',
    tags: ['DEI', 'Inclusive Hiring', 'Tech Teams'],
  },
];

const categories: Category[] = ['All', 'Industry Insights', 'Case Studies', 'Hiring Trends', 'Leadership', 'Compliance'];

const categoryColors: Record<Exclude<Category, 'All'>, { bg: string; text: string }> = {
  'Industry Insights': { bg: '#EFF8FF', text: '#0091DD' },
  'Case Studies': { bg: '#F0FDF4', text: '#16A34A' },
  'Hiring Trends': { bg: '#FFF7ED', text: '#C2410C' },
  Leadership: { bg: '#FAF5FF', text: '#7C3AED' },
  Compliance: { bg: '#FFF1F2', text: '#BE123C' },
};

function CategoryBadge({ category }: { category: Exclude<Category, 'All'> }) {
  const colors = categoryColors[category];
  return (
    <span
      className="inline-flex items-center px-3 py-1 text-xs font-semibold"
      style={{ background: colors.bg, color: colors.text, fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.04em' }}
    >
      {category}
    </span>
  );
}

function AuthorAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .join('');
  const colors = ['#00A7FF', '#0091DD', '#01142D', '#1F65AB'];
  const colorIndex = name.charCodeAt(0) % colors.length;
  return (
    <div
      className="w-8 h-8 flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
      style={{ background: colors[colorIndex], fontFamily: 'Montserrat, sans-serif' }}
    >
      {initials}
    </div>
  );
}

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const featuredPosts = posts.filter((p) => p.featured);
  const filteredPosts = posts.filter(
    (p) => !p.featured && (activeCategory === 'All' || p.category === activeCategory)
  );

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail('');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section
        className="relative flex items-end"
        style={{ paddingTop: '144px', paddingBottom: '64px', background: 'linear-gradient(135deg, #01142D 0%, #0091DD 60%, #00A7FF 100%)' }}
      >
        {/* Decorative grid */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        <div className="container-custom relative z-10 w-full">
          <div className="max-w-3xl">
            <span className="section-label" style={{ color: 'rgba(255,255,255,0.7)' }}>
              TranceformRIGHT Insights
            </span>
            <h1
              className="mt-3 text-white leading-tight"
              style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 200, fontSize: 'clamp(32px, 5vw, 56px)' }}
            >
              Industry Intelligence.
              <br />
              <span style={{ fontWeight: 600 }}>Real-World Results.</span>
            </h1>
            <p className="mt-5 text-white/80 text-lg max-w-xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Case studies, hiring trends, compliance guides, and leadership perspectives from the front lines of IT staffing and workforce strategy.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {(['Case Studies', 'Industry Insights', 'Hiring Trends'] as const).map((tag) => (
                <button
                  key={tag}
                  onClick={() => setActiveCategory(tag)}
                  className="px-4 py-2 text-sm font-semibold text-white border border-white/30 hover:bg-white/10 transition-colors duration-200"
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <div style={{ background: '#0091DD' }}>
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/20">
            {[
              { value: '9+', label: 'Articles Published' },
              { value: '3', label: 'Case Studies' },
              { value: '5', label: 'Topic Categories' },
              { value: 'Weekly', label: 'New Insights' },
            ].map((stat) => (
              <div key={stat.label} className="py-5 px-6 text-center">
                <div className="text-white font-bold text-xl" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {stat.value}
                </div>
                <div className="text-white/70 text-xs mt-0.5" style={{ fontFamily: 'Montserrat, sans-serif', letterSpacing: '0.05em' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Featured Posts */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="flex items-center gap-4 mb-10">
            <div>
              <span className="section-label">Featured</span>
              <h2 className="section-title mt-1">Must-Read Articles</h2>
            </div>
            <div className="flex-1 h-px bg-gray-100 hidden md:block" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {featuredPosts.map((post, idx) => (
              <article
                key={post.id}
                className="card-pinnacle group cursor-pointer flex flex-col"
                style={{ minHeight: idx === 0 ? '420px' : '380px' }}
              >
                {/* Color accent bar */}
                <div
                  className="h-1 w-full"
                  style={{ background: idx === 0 ? '#00A7FF' : '#0091DD' }}
                />
                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <CategoryBadge category={post.category} />
                    <span className="text-gray-400 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {post.readTime}
                    </span>
                  </div>
                  <h3
                    className="text-xl font-semibold text-gray-900 leading-snug mb-3 group-hover:text-pinnacle-blue transition-colors duration-200"
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    {post.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed flex-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {post.excerpt}
                  </p>
                  <div className="mt-6 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <AuthorAvatar name={post.author} />
                      <div>
                        <div className="text-xs font-semibold text-gray-800" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          {post.author}
                        </div>
                        <div className="text-xs text-gray-400" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                          {post.date}
                        </div>
                      </div>
                    </div>
                    <span
                      className="text-sm font-semibold text-pinnacle-blue group-hover:underline"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      Read Article →
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* All Posts with Filter */}
      <section className="section-padding" style={{ background: '#FAFAFA' }}>
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
            <div>
              <span className="section-label">Browse</span>
              <h2 className="section-title mt-1">All Insights</h2>
            </div>
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className="px-4 py-2 text-xs font-semibold transition-all duration-200"
                  style={{
                    fontFamily: 'Montserrat, sans-serif',
                    background: activeCategory === cat ? '#00A7FF' : 'white',
                    color: activeCategory === cat ? 'white' : '#48494B',
                    border: activeCategory === cat ? '1px solid #00A7FF' : '1px solid #e8e8e8',
                  }}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {filteredPosts.length === 0 ? (
            <div className="text-center py-20 text-gray-400" style={{ fontFamily: 'Poppins, sans-serif' }}>
              No articles in this category yet. Check back soon.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post) => (
                <article key={post.id} className="card-pinnacle group cursor-pointer flex flex-col bg-white">
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <CategoryBadge category={post.category} />
                    </div>
                    <h3
                      className="text-base font-semibold text-gray-900 leading-snug mb-2 group-hover:text-pinnacle-blue transition-colors duration-200"
                      style={{ fontFamily: 'Poppins, sans-serif' }}
                    >
                      {post.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed flex-1 line-clamp-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      {post.excerpt}
                    </p>
                    <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AuthorAvatar name={post.author} />
                        <div>
                          <div className="text-xs font-semibold text-gray-700" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            {post.author}
                          </div>
                          <div className="text-xs text-gray-400" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            {post.readTime} · {post.date}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {post.tags.map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs text-gray-500 bg-gray-100"
                          style={{ fontFamily: 'Montserrat, sans-serif' }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="section-padding" style={{ background: '#01142D' }}>
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <span className="section-label" style={{ color: '#00A7FF' }}>
              Stay Ahead
            </span>
            <h2
              className="mt-3 text-white leading-tight"
              style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 200, fontSize: 'clamp(26px, 4vw, 42px)' }}
            >
              Get IT Workforce Insights
              <br />
              <span style={{ fontWeight: 600 }}>Delivered to Your Inbox</span>
            </h2>
            <p className="mt-4 text-white/70 text-base" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Monthly briefings on hiring trends, compliance updates, and staffing strategies — no fluff, no spam. Used by 1,200+ HR and technology leaders.
            </p>
            {subscribed ? (
              <div
                className="mt-8 inline-flex items-center gap-3 px-8 py-4 bg-white/10 text-white text-sm font-semibold"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                You're subscribed — welcome aboard.
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your work email address"
                  className="input-field flex-1"
                  style={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}
                />
                <button type="submit" className="btn-primary whitespace-nowrap">
                  Subscribe Free
                </button>
              </form>
            )}
            <p className="mt-4 text-white/40 text-xs" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Unsubscribe anytime. We respect your inbox.
            </p>
          </div>
        </div>
      </section>

      {/* CTA to Contact */}
      <section className="py-16 bg-white">
        <div className="container-custom text-center">
          <h3
            className="text-2xl font-light text-gray-800 mb-2"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Ready to put these insights into action?
          </h3>
          <p className="text-gray-500 mb-6 text-sm" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Talk to a TranceformRIGHT strategist about your specific hiring challenges.
          </p>
          <Link href="/contact" className="btn-primary">
            Schedule a Consultation
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
